/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { memo, useEffect, useCallback } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useRoutes
} from "react-router-dom";
import { findIndex, map } from "lodash";
import { Layout } from "_metronic/layout";
import { AuthRedirectGuardMiddleware } from "lib/gmjs";
import AppConfiguration from "dataProvider/config";
import { useDispatch, useSelector } from "react-redux";
import { setAuthenticated } from "./identity/_redux/actions";

import Admin from "app/modules/admin/loadable";
import Camera from "app/modules/camera/loadable";
import Truck from "app/modules/truck/loadable";
import Identity from "app/modules/identity/loadable";
import Printer from "app/modules/printer/loadable";
import Sms from "app/modules/sms/loadable";
import Tripod from "app/modules/tripod/loadable";
import Barrier from "app/modules/barrier/loadable";
import SmartLock from "app/modules/smart-lock/loadable";
import SmartScale from "app/modules/smart-scale/loadable";
import Mobile from "app/modules/mobile/loadable";
import Lego from "app/modules/lego/loadable";
import CrossbeltBombay from "app/modules/crossbelt-bombay/loadable";
import { makeSelectListStrictedRoute } from "./app/_redux/selectors";
import FlashScanner from "app/modules/flash-scanner/loadable";
import PutToLight from "app/modules/put-to-light/loadable";
import WheelSorter from "app/modules/wheel-sorter/loadable";
import RFID from "app/modules/rfid/loadable";
import CapacitoringDataPage from "./admin/pages/capacitoring-data";
import AdminRoutes, { DeviceRoutes } from "./admin/admin.routes";
import CameraRoutes from "./camera/camera.routes";
import TruckRoutes from "./truck/truck.routes";
import PrinterRoutes from "./printer/printer.routes";
import SmsRoutes from "./sms/sms.routes";
import TripodRoutes from "./tripod/tripod.routes";
import BarrierRoutes from "./barrier/barrier.routes";
import SmartLockRoutes from "./smart-lock/smartLock.routes";
import SmartScaleRoutes, {
  SmartScaleV2Routes
} from "./smart-scale/smartScale.routes";
import FlashScannerRoutes from "./flash-scanner/flashScanner.routes";
import MobileRoutes from "./mobile/mobile.routes";
import LegoRoutes from "./lego/lego.routes";
import CrossbeltBombayRoutes from "./crossbelt-bombay/crossbelt-bombay.routes";
import PutToLightRoutes from "./put-to-light/putToLight.routes";
import WheelSorterRoutes from "./wheel-sorter/wheelSorter.routes";
import RfidRoutes from "./rfid/rfid.routes";
import IdentityRoutes from "./identity/identity.routes";
import Callback from "./identity/pages/callback";
import IdentityComponent from "./identity";
import NotRoute from "./identity/pages/not-route";
import Wms from "./wms/loadable";
import { WmsRoutes } from "./wms/wms.routes";
import ModuleSorting from "./module-sorting/loadable";
import ModuleSortingRoutes from "./module-sorting/module-sorting.routes";
import DeviceDetailPage from "./admin/pages/capacitoring-data/device-detail";
import NotPermission from "./identity/pages/not-permission";
import CallCenter from "./call-center/loadable";
import CallCenterRoutes from "./call-center/call-center.routes";
import TempSensor from "./temperature/loadable";
import TempSensorRoutes from "./temperature/temperature.routes";
import CameraConfigRoutes from "./camera-config/camera-config.router";
import CameraConfig from "./camera-config/loadable";

const protectedRoutes = {
  Admin: {
    path: "/system",
    Component: Admin,
    children: AdminRoutes
  },
  Camera: {
    path: "/camera",
    Component: Camera,
    children: CameraRoutes
  },
  Truck: {
    path: "/truck",
    Component: Truck,
    children: TruckRoutes
  },
  printer: {
    path: "/printer",
    Component: Printer,
    children: PrinterRoutes
  },
  sms: {
    path: "/sms",
    Component: Sms,
    children: SmsRoutes
  },
  tripod: {
    path: "/tripod",
    Component: Tripod,
    children: TripodRoutes
  },
  barrier: {
    path: "/barrier",
    Component: Barrier,
    children: BarrierRoutes
  },
  smartLock: {
    path: "/smartLock",
    Component: SmartLock,
    children: SmartLockRoutes
  },
  smartScale: {
    path: "/smartScale",
    Component: SmartScale,
    children: SmartScaleRoutes
  },
  smartScaleV2: {
    path: "/smart-scale-v2",
    Component: SmartScale,
    children: SmartScaleV2Routes
  },
  flashScanner: {
    path: "/flash-scanner",
    Component: FlashScanner,
    children: FlashScannerRoutes
  },
  mobile: {
    path: "/mobile",
    Component: Mobile,
    children: MobileRoutes
  },
  lego: {
    path: "/lego",
    Component: Lego,
    children: LegoRoutes
  },
  crossbeltBombay: {
    path: "/crossbelt-bombay",
    Component: CrossbeltBombay,
    children: CrossbeltBombayRoutes
  },
  putToLight: {
    path: "/put-to-light",
    Component: PutToLight,
    children: PutToLightRoutes
  },
  wheelSorter: {
    path: "/wheel-sorter",
    Component: WheelSorter,
    children: WheelSorterRoutes
  },
  rfid: {
    path: "/rfid",
    Component: RFID,
    children: RfidRoutes
  },
  profile: {
    path: "/device",
    Component: Admin,
    children: DeviceRoutes
  },
  wms: {
    path: "/wms",
    Component: Wms,
    children: WmsRoutes
  },
  moduleSorting: {
    path: "/module-sorting",
    Component: ModuleSorting,
    children: ModuleSortingRoutes
  },
  callCenter: {
    path: "/call-center",
    Component: CallCenter,
    children: CallCenterRoutes
  },
  temperature: {
    path: "/temp-sensor",
    Component: TempSensor,
    children: TempSensorRoutes
  },
  cameraConfig: {
    path: "/authorization-camera",
    Component: CameraConfig,
    children: CameraConfigRoutes
  }
};
const authRedirectGuardMiddleware = AuthRedirectGuardMiddleware(
  AppConfiguration.ghtkID.oidc
);

const ModulesRoutes = memo(({ isAuthenticated }) => {
  let location = useLocation();
  const dispatch = useDispatch();
  const listStrictedRoute = useSelector(makeSelectListStrictedRoute);
  const verifyPathWithRole = (path = location.pathname) => {
    return (
      findIndex(listStrictedRoute, x => {
        return path.indexOf(x.path) > -1;
      }) > -1 ||
      path == "/smartLock/admin-manage" ||
      path == "/"
    );
  };

  const handleAuthGuardMiddleWare = useCallback(() => {
    if (location.pathname == "/oauth2/callback") return;
    authRedirectGuardMiddleware.run(
      location.pathname + (location.search || ""),
      () => {
        dispatch(setAuthenticated({ isAuthenticated: true }));
      }
    );
  }, [dispatch, location]);

  let protectedElement = useRoutes([
    ...map(protectedRoutes, route => {
      let Component = route.Component;
      return {
        path: route.path,
        element: <Component />,
        children: route.children
      };
    }),

    { path: "/oauth2", element: <Identity />, children: IdentityRoutes },
    {
      path: "/system/admin/dashboard-device",
      element: <CapacitoringDataPage />
    },
    {
      path: "/system/admin/dashboard-device/:station/:device",
      element: <DeviceDetailPage />
    },
    { path: "/", element: <Navigate to="/system/admin/dashboard-device" /> },
    {
      path: "*",
      element: <NotPermission />
    }
  ]);

  useEffect(() => handleAuthGuardMiddleWare(), [handleAuthGuardMiddleWare]);

  if (isAuthenticated) {
    if (listStrictedRoute.length === 0) {
      return (
        <Layout>
          <Routes>
            <Route path="*" element={<NotRoute />}></Route>
          </Routes>
        </Layout>
      );
    }
    let isVerifyPathWithRole = verifyPathWithRole();

    if (isVerifyPathWithRole == false) {
      return (
        <Layout>
          <Routes>
            <Route path="*" element={<NotPermission />}></Route>
          </Routes>
        </Layout>
      );
    } else {
      return <Layout>{protectedElement}</Layout>;
    }
  } else {
    return (
      <Routes>
        <Route element={<IdentityComponent />} path="/oauth2">
          <Route path="callback" element={<Callback />}></Route>
        </Route>
        <Route
          path="*"
          element={
            <Layout>
              <NotRoute />
            </Layout>
          }
        ></Route>
      </Routes>
    );
  }
});

export default ModulesRoutes;
